import Vue from "vue";
import getResidref from "@/utils/remoteMedia";

export interface TextMetadata {
  versionCreated: string; // "2020-04-10T22:10:03.000+01:00" Always with offset
  genre?: string;
  genreCode?: string;
}

export interface PictureMetadata {
  versionCreated: string; // "2020-04-10T22:10:03.000+01:00" Always with offset
  genre?: string;
  genreCode?: string;
  thumbnail: string;
}

export interface GraphicMetadata {
  versionCreated: string; // "2020-04-10T22:10:03.000+01:00" Always with offset
  genre?: string;
  genreCode?: string;
  thumbnail: string;
}

export interface ValueUnit {
  id: string;
  title: string;
  previewUrl: string;
  type: "picture" | "graphic" | "text";
  textMetadata?: TextMetadata;
  pictureMetadata?: PictureMetadata;
  graphicMetadata?: GraphicMetadata;
  location?: {
    country: string;
    region: string;
  };
}

const appendThumbnail = async ({ event, encodedImages, dispatch }) => {
  const { itemClass, guid, remoteContent } = event;
  const hasPreview = itemClass === "picture" || itemClass === "graphic";
  let thumbnail;
  if (hasPreview) {
    if (encodedImages[guid] && encodedImages[guid].preview) {
      thumbnail = encodedImages[guid].preview;
    } else {
      const residref = getResidref(remoteContent, itemClass);
      await dispatch(
        "newsItems/fetchImage",
        {
          isThumbnail: false,
          residref,
          guid
        },
        { root: true }
      );
      thumbnail = encodedImages[guid].preview;
    }
  }
  return { ...event, thumbnail };
};

// eslint-disable-next-line no-shadow
enum MetadataType {
  text = "textMetadata",
  picture = "pictureMetadata",
  graphic = "graphicMetadata"
}

/**
 * Reduces the newsitem object into only data needed to it on the collaboration service.
 * @param {Object} newsitem Newsitem object.
 * @returns {ValueUnit} Value unit
 */
export const reduceToMinimum = (newsitem): ValueUnit => {
  const valueUnitType = newsitem.itemClass;
  const metadataType = MetadataType[valueUnitType];
  const genreMetadata = newsitem.genrecodes.length
    ? {
        genre: newsitem.genre[0],
        genreCode: newsitem.genrecodes[0].uri
      }
    : {};
  const imageMetadata = newsitem.thumbnail
    ? { thumbnail: newsitem.thumbnail }
    : {};

  return {
    id: newsitem.guid,
    title: newsitem.headline[0],
    previewUrl: `https://cr.dpa-connect.de/v1/item/${newsitem.guid}`,
    type: valueUnitType,
    [metadataType]: {
      versionCreated: newsitem.versionCreated,
      ...genreMetadata,
      ...imageMetadata
    }
  };
};

export default {
  state: {
    selectedEvents: {},
    isBatchSelected: false,
    isSidebarVisible: false,
    selectedFolder: null,
    dialogEventId: undefined
  },
  getters: {
    isSelectionEnabled: ({ selectedEvents }) =>
      Object.keys(selectedEvents).length > 0,
    isIdSelected: ({ selectedEvents }) => id => {
      return Object.prototype.hasOwnProperty.call(selectedEvents, id);
    },
    numberOfSelectedEvents: ({ selectedEvents }) => {
      return Object.keys(selectedEvents).length;
    },
    formattedSelectedEvents({ selectedEvents }) {
      return Object.entries(selectedEvents).map(([, v]) => {
        return {
          type: "folderListItem",
          itemValueUnit: v
        };
      });
    }
  },
  mutations: {
    addEvent: ({ selectedEvents }, { id, event }) => {
      Vue.set(selectedEvents, id, event);
    },
    removeEvent: ({ selectedEvents }, id) => {
      if (selectedEvents[id]) Vue.delete(selectedEvents, id);
    },
    clearSelection: state => {
      state.selectedEvents = {};
      state.isBatchSelected = false;
    },
    toggleBatch: state => {
      state.isBatchSelected = !state.isBatchSelected;
    },
    unselectBatch: state => {
      if (state.isBatchSelected) {
        state.isBatchSelected = false;
      }
    },
    closeSidebar: state => {
      state.isSidebarVisible = false;
    },
    openSidebar: state => {
      state.isSidebarVisible = true;
    },
    setSelectedFolder: (state, folder) => {
      state.selectedFolder = folder;
    },
    setEvents(state, payload) {
      state.selectedEvents = {
        ...payload
      };
    },
    setDialogEventId: (state, id) => {
      state.dialogEventId = id;
    }
  },
  actions: {
    fetchThumbnailForNewsItem: async ({ rootState, dispatch }, { event }) => {
      await appendThumbnail({
        event,
        encodedImages: rootState.newsItems.encodedImages,
        dispatch
      });
    },

    addEvent: async ({ state, rootState, commit, dispatch }, { event }) => {
      const { encodedImages } = rootState.newsItems;
      const { guid } = event;
      const formattedEvent = await appendThumbnail({
        event,
        encodedImages,
        dispatch
      });
      const reducedEvent = reduceToMinimum(formattedEvent);
      if (!Object.prototype.hasOwnProperty.call(state.selectedEvents, guid)) {
        commit("addEvent", { id: guid, event: reducedEvent });
      }
    },
    selectVisibleEvents: async ({ state, rootState, commit, dispatch }) => {
      const { newsItems } = rootState.newsItems;
      const { encodedImages } = rootState.newsItems;
      const formattedEvents: ValueUnit[] = await Promise.all(
        newsItems
          .map(event =>
            appendThumbnail({
              event,
              encodedImages,
              dispatch
            })
          )
          .map(async eventPromise => reduceToMinimum(await eventPromise))
      );
      const eventsAsObj = formattedEvents.reduce((obj, event) => {
        return { ...obj, [event.id]: event };
      }, {});
      const newEvents = {
        ...state.selectedEvents,
        ...eventsAsObj
      };
      commit("setEvents", newEvents);
    },
    unselectVisibleEvents: ({ state, rootState, commit }) => {
      const temp = {
        ...state.selectedEvents
      };
      const { newsItems } = rootState.newsItems;
      newsItems.forEach(event => {
        if (temp[event.guid]) delete temp[event.guid];
      });
      commit("setEvents", temp);
    }
  },
  namespaced: true
};
